import React, { ChangeEvent, memo, MouseEvent, useEffect, useRef, useState } from 'react';
import cls from '@utils/css-select';
import { history } from '@store/history';
import styles from './styles.scss';
import Section from 'components/section';
import Input from 'components/input';
import Select, { Option } from 'components/select';
import { AGREEMENT_POLICY, AGREEMENT_USER, APPLY_SUCCESS } from 'constants/url';
import { fetchApplyTutor, getLocationIp, getSubjectDictInfo } from '@services/appointment';
import { useSelector } from 'react-redux';
import { ConnectState } from 'states/connect';
import { toast } from 'components/dialogs';
import { COUNTRY_TEXT_REG, COUNTRY_CODE_REG } from 'constants/country';
import { qsParse } from '@utils/url-utils';
import { sendEvent } from '@utils/core/track';
import { BannerImg, BannerTitle } from '../../imgUrls';
import { getChannelInfo } from '@utils/channel-util';
import generatePath from '@utils/generatePath';

const cx = cls(styles);

type GradeItem = {
	gradeName: string;
	gradeCode: string;
	subjectList: SubjectItem[];
};

type SubjectItem = {
	subjectCode: string;
	subjectName: string;
};

type AreaCodeItem = {
	code: string;
	text: string;
};

/**
 * 接口请求传输
 *
 * @interface IFormDataParams
 */
interface IFormDataParams {
	subjectCode: string;
	gradeCode: string;
	areaCode: string | number;
	areaText: string;
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
}
const TraceMap: {
	[propName: string]: { eventId: string };
} = {
	areaCode: { eventId: 'en_offWebsite0811_AreaCodeSelect' },
	gradeCode: { eventId: 'en_offWebsite0811_Grade' },
	subjectCode: { eventId: 'en_offWebsite0811_SubjectSelect' },
};
/**
 * 获取国家区号
 * @returns
 */
function handleAreaCode() {
	return Object.keys(COUNTRY_TEXT_REG).reduce((pre: Record<string, any>, next) => {
		pre[next] = {
			text: COUNTRY_TEXT_REG[next],
			code: Object.keys(COUNTRY_CODE_REG).find((key) => COUNTRY_CODE_REG[key].find((code) => code === next)) || '',
		};
		return pre;
	}, {});
}

const areacCodeMap: Record<string, AreaCodeItem> = handleAreaCode();
const regexp = /^[a-zA-Z]+$/;
/**
 * 头部预约表单
 */
export default memo(function FromTable() {
	const { local, countryCode } = useSelector((state: ConnectState) => state.app);
	const [areaList, setAreaList] = useState<AreaCodeItem[]>([]);
	const [gradeList, setGradeList] = useState<GradeItem[]>([]);
	const [subjectList, setSubjectList] = useState<SubjectItem[]>([]);
	const [formParams, setFormParams] = useState<IFormDataParams>({
		areaCode: '',
		areaText: '',
		firstName: '',
		lastName: '',
		phone: '',
		email: '',
		gradeCode: '',
		subjectCode: '',
	});
	const { channel_code: code, channel_keyword: key } = getChannelInfo(countryCode);
	const checkRef = useRef<HTMLInputElement>(null);
	const tags = [{ strong: '500,000+', info: 'paid students' }, { strong: '10,000+', info: 'tutors' }, { info: 'SATs | GCSE | A-Level' }];
	// 切换语言 英国官网
	const CID = 11;
	const emailRules = /^\w+([.-]\w+)*@\w+([.-]\w+)*\.\w+$/
	/**
	 * 预约注册
	 */
	function handleSubmit() {
		sendEvent({
			eventId: 'en_offWebsite0811_ClickSubmit',
			eventParam: {
				result: '',
			},
		});
		// 表单校验
		if (!formParams.firstName || !formParams.lastName) {
			toast({
				message: 'Errors in first name/last name',
			});
			return;
		}
		// policy
		if (!checkRef.current?.checked) {
			toast({
				message: 'Please read and consent to the User Agreement and Privacy Policy',
			});
			return;
		}
		if (!formParams.phone) {
			toast({
				message: 'Please fill in the phone number',
			});
			return;
		}
		if (formParams.email !== '' && !emailRules.test(formParams.email as string)) {
			toast({
				message: 'Please check the email',
			});
			return;
		}
		if (!formParams.gradeCode || !formParams.subjectCode) {
			toast({
				message: 'Please select grade and subject',
			});
			return;
		}
		fetchApplyTutor(CID, {
			name: formParams.firstName + ' ' + formParams.lastName,
			mobile: formParams.phone,
			email: formParams.email,
			subjectCode: formParams.subjectCode,
			gradeCode: formParams.gradeCode,
			areaCode: formParams.areaCode,
			code,
			key,
		})
			.then((res) => {
				sendEvent({
					eventId: 'en_offWebsite0811_SubmitResult',
					eventParam: {
						result: res.message,
					},
				});
				window.gtag('event', 'conversion', { send_to: 'AW-328519748/ZImKCMzq5t4CEMSg05wB' });
				window.gtag('event', 'conversion', { send_to: 'AW-346007589/erxeCPOq7d4CEKXQ_qQB' });
				window.gtag('event', 'conversion', { send_to: 'AW-328316016/rUbiCJHr0t4CEPDoxpwB' });
				window.gtag('event', 'conversion', { send_to: 'AW-327291236/tAqYCJqFqd8CEOSiiJwB' });
				window.gtag('event', 'conversion', { send_to: 'AW-327291257/WXLrCPG7qd8CEPmiiJwB' });
				window.gtag('event', 'conversion', { send_to: 'AW-327802922/W5LcCOvD1t8CEKrAp5wB' });
				window.gtag('event', 'conversion', { send_to: 'AW-327843671/x22XCNyp-N4CENf-qZwB' });
				window.gtag('event', 'conversion', { send_to: 'AW-396168788/3rW2CImf_OICENSc9LwB' });
				window.fbq('track', 'Lead');
				history.push({
					pathname: APPLY_SUCCESS,
					search: `?channel_code=${code}&channel_keyword=${key}`,
					state: { nation: local },
				});
			})
			.catch((err) => {
				sendEvent({
					eventId: 'en_offWebsite0811_SubmitResult',
					eventParam: {
						result: err.message,
					},
				});
				toast({
					message: err.message || 'System error. Try again',
				});
			});
	}
	/**
	 * 值变化
	 * @param e
	 */
	function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
		const { key = '' } = e.currentTarget.dataset;
		const val = e.currentTarget.value;
		// 年级科目级联
		if (key === 'gradeCode') {
			const gradeObject = gradeList.find((v) => v.gradeCode === val) || { subjectList: [] };
			setSubjectList(gradeObject?.subjectList);
			setFormParams({
				...formParams,
				subjectCode: '',
				[key]: val,
			});
			return;
		}
		setFormParams({
			...formParams,
			[key]: val,
		});
	}
	function handleInputChange(key: string, val: string) {
		// 年级科目级联
		if (key === 'gradeCode') {
			const gradeObject = gradeList.find((v) => v.gradeCode === val) || { subjectList: [] };
			setSubjectList(gradeObject?.subjectList);
			setFormParams({
				...formParams,
				subjectCode: '',
				[key]: val,
			});
			return;
		}
		setFormParams({
			...formParams,
			[key]: val,
		});
	}

	/**
	 *埋点处理
	 * @param null e
	 */
	function handleTrack(e: any) {
		const { evid = '', key = '' } = e.currentTarget.dataset;
		if (!key) {
			sendEvent({
				eventId: evid,
			});
			return;
		}
		sendEvent({
			eventId: evid,
			eventParam: {
				[key.toLocaleLowerCase()]: e.currentTarget.value,
			},
		});
	}
	function handleInputTrack(evid: string, key: string, value: string) {
		if (!key) {
			sendEvent({
				eventId: evid,
			});
			return;
		}
		sendEvent({
			eventId: evid,
			eventParam: {
				[key.toLocaleLowerCase()]: value,
			},
		});
	}

	/**
	 * 区域选择 年级 科目
	 * @param e
	 */
	function onSelectChange(option: { code: string; text: string }, key: string) {
		sendEvent({
			eventId: TraceMap[key]?.eventId,
			eventParam: {
				[key.toLocaleLowerCase()]: option.code,
			},
		});
		const val = option.code;
		if (key === 'gradeCode') {
			const gradeObject = gradeList.find((v) => v.gradeCode === val) || { subjectList: [] };
			setSubjectList(gradeObject?.subjectList);
			setFormParams({
				...formParams,
				subjectCode: '',
				gradeCode: val as string,
			});
			return;
		}
		const newFormParams = {
			...formParams,
			[key]: val,
		};
		if (key === 'areaCode') {
			newFormParams.areaText = option.text as string;
		}
		setFormParams(newFormParams);
	}

	useEffect(() => {
		// 获取本地IP
		getLocationIp()
			.then((res) => {
				setFormParams({
					...formParams,
					areaCode: areacCodeMap[res.countryId || 'GB'].code,
					areaText: areacCodeMap[res.countryId || 'GB'].text,
				});
				setAreaList(Object.values(areacCodeMap));
			})
			.catch(() => {
				setFormParams({
					...formParams,
					areaCode: areacCodeMap['GB'].code,
					areaText: areacCodeMap['GB'].text,
				});
				setAreaList(Object.values(areacCodeMap));
			});
	}, []);

	useEffect(() => {
		// 获取年级科目
		getSubjectDictInfo(CID)
			.then((res) => {
				setGradeList(res.dictInfo);
				setSubjectList(res.dictInfo[0]?.subjectList);
			})
			.catch((err) => {
				toast({
					message: err.message || 'System error. Try again',
				});
			});
	}, []);
	return (
		<div className={cx('form-table')}>
			<Section>
				<div className={cx('circle', 's1')} />
				<div className={cx('circle', 's2')} />
				<div className={cx('circle', 's3')} />
				<h1 className={cx('logo')}>
					<img loading="lazy" src={BannerTitle} alt="Deliver results quickly - Airclass online tutoring" />
				</h1>
				<div className={cx('tags')}>
					{tags.map((tag, idx) => {
						return (
							<div className={cx('tag')} key={idx}>
								{tag.strong && <b>{tag.strong}</b>} {tag.info}
							</div>
						);
					})}
				</div>
				<div className={cx('form-body')} id="form-ele">
					<div className={cx('form-img')}>
						<img loading="lazy" src={BannerImg} alt="air-class" />
					</div>
					<div className={cx('form-group')}>
						<legend className={cx('title')}>
							Get a 1-on-1 trial session for
							<b>&nbsp; FREE</b>
						</legend>
						<div className={cx('form-control')}>
							<Input
								className={cx('input')}
								placeholder="First Name"
								// placeholder={<span>First Name <strong>*</strong></span>}
								value={formParams.firstName}
								size='large'
								onChange={(val: string) => handleInputChange('firstName', val)}
								onBlur={() => handleInputTrack('en_offWebsite0811_NameInput', 'firstName', formParams.firstName)}
							/>
							<Input
								className={cx('input')}
								placeholder="Last Name"
								// placeholder={<span>Last Name <strong>*</strong></span>}
								value={formParams.lastName}
								size='large'
								onChange={(val: string) => handleInputChange('lastName', val)}
								onBlur={() => handleInputTrack('en_offWebsite0811_LastNameInput', 'lastName', formParams.lastName)}
							/>
						</div>
						<div className={cx('form-control')}>
							<div>
								<Select
									className={cx('grade-select')}
									placeholder='Grade'
									// placeholder={<span>Grade <strong>*</strong></span>}
									searchPlaceholder="Search"
									selected={{ value: formParams.gradeCode, label: '' }}
									onChange={(option) => onSelectChange({ code: option.value, text: option.label }, 'gradeCode')}
									allowSearch={false}
									size='large'
									showArrow={true}>
									{gradeList.map((v) => {
										return (
											<Option value={v.gradeCode} key={v.gradeCode} option={{ ...v, label: v.gradeName, value: v.gradeCode }}>
												{v.gradeName}
											</Option>
										);
									})}
								</Select>
							</div>
							<div>
								<Select
									className={cx('subject-select')}
									placeholder='Subject'
									// placeholder={<span>Subject <strong>*</strong></span>}
									selected={{ value: formParams.subjectCode, label: '' }}
									onChange={(option) => onSelectChange({ code: option.value, text: option.label }, 'subjectCode')}
									allowSearch={false}
									size='large'
									showArrow={true}>
									{subjectList.map((v) => {
										return (
											<Option value={v.subjectCode} key={v.subjectCode} option={{ ...v, label: v.subjectName, value: v.subjectCode }}>
												{v.subjectName}
											</Option>
										);
									})}
								</Select>
							</div>
						</div>
						<div className={cx('form-control')}>
							<div className={cx('input', 'phone')}>
								<div>
									<Select
										className={cx('area-select')}
										placeholder="select area"
										searchPlaceholder="Search"
										selected={{ value: formParams.areaText, label: formParams.areaText }}
										onChange={(option) => onSelectChange({ code: (option as any).code, text: option.label }, 'areaCode')}
										allowSearch
										showArrow={false}>
										{areaList.map((v, idx) => {
											return (
												<Option value={v.text} key={v.text} option={{ ...v, label: v.text, value: v.text }}>
													{v.text}
												</Option>
											);
										})}
									</Select>
								</div>
								<span className={cx('split-v')} />
								<Input
									className={cx('input', 'phone-number')}
									placeholder='Mobile phone number'
									value={formParams.phone}
									prefix={<span className={cx('code-txt')}>+{formParams.areaCode}</span>}
									size='large'
									onChange={(val: string) => handleInputChange('phone', val)}
									onBlur={() => handleInputTrack('en_offWebsite0811_PhoneInput', 'phone', formParams.phone)}
								/>
							</div>
						</div>
						<div className={cx('form-control')}>
							<Input
								className={cx('input', 'email')}
								placeholder='Email'
								value={formParams.email}
								size='large'
								onChange={(val: string) => handleInputChange('email', val)}
								onBlur={() => handleInputTrack('en_offWebsite0811_EmailInput', 'email', formParams.email)}
							/>
						</div>
						<div className={cx('agreement-box')}>
							<input
								className={cx('check-box')}
								type="checkbox"
								ref={checkRef}
								data-evid="en_offWebsite0811_Agreement"
								onFocus={handleTrack}
							/>
							<p>
								Consent to{' '}
								<span
									onClick={() => {
										history.push({ pathname: generatePath(AGREEMENT_USER), state: { nation: local } });
									}}>
									User Agreement
								</span>{' '}
								and{' '}
								<span
									onClick={() => {
										history.push({ pathname: generatePath(AGREEMENT_USER), state: { nation: local } });
									}}>
									Privacy Policy
								</span>
							</p>
						</div>
						<div className={cx('submit-btn')} onClick={handleSubmit}>
							Request Now!
						</div>
					</div>
				</div>
			</Section>
		</div>
	);
});
